import {
    ContentType,
    GroupType,
    IGroupPictureFileMetadataResponse,
    IPictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import {
    buildOrderBy,
    constructSearchExpression,
    Filter,
    toDataSourceResult,
} from 'utils/query_v3';

export type FileLibraryFilter = Readonly<{
    groupIds?: string[] | undefined;
    locationId?: string | undefined;
    corporationId?: string | undefined;
    nameSearch: string | undefined;
    fileTypes?: ContentType[] | undefined;
    excludeFolders?: boolean | undefined;
    parentFolderId?: string | undefined;
    isRootFolder?: boolean;
}>;

export type UseFileLibraryDataSourceOptions = BaseDataSourceOptions<
    IPictureFileMetadataResponse,
    FileLibraryFilter
>;

const getAllFiles: DataSourceQuery<IPictureFileMetadataResponse, FileLibraryFilter> = ({
    filter: {
        groupIds,
        nameSearch,
        locationId,
        corporationId,
        fileTypes,
        excludeFolders,
        parentFolderId,
        isRootFolder,
    },
    sort,
    page,
    pageLimit,
}) => {
    const filter = Filter.and(
        locationId ? Filter.eq('LocationId', locationId) : undefined,
        // We cannot supply BOTH a corporationId AND locationId to the API
        corporationId && !locationId
            ? Filter.eq('CorporationId', corporationId)
            : undefined,
        fileTypes ? Filter.in('FileType', fileTypes) : undefined,
        excludeFolders ? Filter.neq('FileType', ContentType.Folder) : undefined,
        parentFolderId ? Filter.eq('ParentFolderId', parentFolderId) : undefined,
        isRootFolder ? Filter.eq('isRootFolder', true) : undefined
    );

    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.getFiles({ groupIds, filter, page, pageLimit, orderBy, nameSearch })
    );
};

export type FileLibraryPagedDataSource = PagedDataSource<
    IPictureFileMetadataResponse,
    FileLibraryFilter
>;

export function usePagedFileLibraryDataSource({
    ...options
}: UseFileLibraryDataSourceOptions): FileLibraryPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getAllFiles,
    });
}

export type FileLibraryInfiniteDataSource = InfiniteDataSource<
    IPictureFileMetadataResponse,
    FileLibraryFilter
>;

export function useInfiniteFileLibraryDataSource({
    ...options
}: UseFileLibraryDataSourceOptions): FileLibraryInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getAllFiles,
    });
}

export const AllFilesGroupTypes: FilesGroupType[] = [
    GroupType.Assembly,
    GroupType.Asset,
    GroupType.MonitoringPoint,
];

export type FilesGroupType =
    | GroupType.Assembly
    | GroupType.Asset
    | GroupType.MonitoringPoint;

export type GroupPicturesFilter = Readonly<{
    groupId: string;
    nameSearch: string | undefined;
    groupType?: FilesGroupType[];
}>;

export type GroupPicturesPagedDataSource = PagedDataSource<
    IGroupPictureFileMetadataResponse,
    GroupPicturesFilter
>;

export type GroupPicturesDataSourceOptions = BaseDataSourceOptions<
    IGroupPictureFileMetadataResponse,
    GroupPicturesFilter
>;

const getGroupPictures: DataSourceQuery<
    IGroupPictureFileMetadataResponse,
    GroupPicturesFilter
> = ({ filter: { groupId, groupType, nameSearch }, sort, page, pageLimit }) => {
    const nameSearchFilterExpr = !nameSearch
        ? undefined
        : constructSearchExpression(nameSearch, ['DisplayName', 'Description']);

    return sdClient
        .getPicturesForGroup({
            filter: Filter.and(
                nameSearchFilterExpr,
                groupType === undefined || groupType === AllFilesGroupTypes
                    ? undefined
                    : Filter.in('GroupType', groupType)
            ),
            orderBy: buildOrderBy(sort),
            groupId,
            includeChildren: true,
            page,
            pageLimit,
        })
        .then(toDataSourceResult);
};

export function usePagedGroupFilesDataSource({
    ...options
}: GroupPicturesDataSourceOptions): GroupPicturesPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getGroupPictures,
    });
}
